// The 360 header's tag line (spec 09 §4.2's E2-2a1 annotation, D-323 ruling
// 4). Every reader of the customer sees its tags as chips. A writer (§7
// narrowing 22: sales on assigned customers, facility_admin, group_admin) on
// an ACTIVE customer also gets a remove button on each chip and an add control
// offering the active tags the customer does not carry yet. These are the
// customer's single verbs, the ones a phone reader uses because the book
// shows no bulk bar below 769 px (spec 09 §4.1).
//
// An archived customer takes no tag write (422 `CUSTOMER_ARCHIVED` on both
// doors), so it is offered none. While the browser is offline the controls
// are disabled, and a refused write reads one line naming why.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { Select } from "../../../../../components/select";
import { RequestError } from "../../../../../lib/request";
import { MutationFailureNote } from "../../mutation-failure-note";
import { useReadsHeldOffline } from "../../lib/reads";
import { canTagCustomers, useSalesPermissions } from "../../hooks/permissions";
import { useCustomerTagVerb, useTagOptions, type CustomerTagRef } from "../../hooks/crm-queries";
import { TagChips } from "./tag-chips";
import { addableTags } from "./tag-options";

/** The tag doors' refusals the reader is told in words (D-225). */
const TAG_REFUSALS = new Set(["TAG_ARCHIVED", "TAG_NOT_FOUND", "CUSTOMER_ARCHIVED"]);

export function tagRefusalKey(error: unknown): string | undefined {
  if (!(error instanceof RequestError)) return undefined;
  if (error.code !== undefined && TAG_REFUSALS.has(error.code)) {
    return `sales.tags.refusal.${error.code}`;
  }
  if (error.httpStatus === 403) return "sales.tags.refusal.forbidden";
  return undefined;
}

export function CustomerTags({
  customerId,
  customerActive,
  tags,
}: {
  customerId: string;
  customerActive: boolean;
  tags: readonly CustomerTagRef[];
}) {
  const { t } = useTranslation();
  const perms = useSalesPermissions();
  const canWrite = customerActive && canTagCustomers(perms);
  const offline = useReadsHeldOffline();
  // Only a writer needs the list: a reader's chips come from the one GET.
  const options = useTagOptions(canWrite);
  const verb = useCustomerTagVerb(customerId);
  const [picked, setPicked] = useState("");
  const addable = addableTags(options.data ?? [], tags);
  const busy = verb.isPending;
  const refusal = verb.isError ? tagRefusalKey(verb.error) : undefined;

  if (tags.length === 0 && !canWrite) return null;

  return (
    <div className="sales-360-tags" data-testid="sales-360-tags">
      <span className="sales-360-fact">{t("sales.tags.title")}:</span>
      {tags.length === 0 ? (
        <span className="sales-hint" data-testid="sales-360-tags-none">
          {t("sales.tags.none")}
        </span>
      ) : (
        <TagChips
          tags={tags}
          action={
            canWrite
              ? (tag) => (
                  <Button
                    size="sm"
                    className="sales-tag-remove"
                    data-testid="sales-360-tag-remove"
                    aria-label={t("sales.tags.remove", { name: tag.name })}
                    disabled={busy || offline}
                    onClick={() => {
                      verb.mutate({ verb: "detach", tagId: tag.id });
                    }}
                  >
                    ×
                  </Button>
                )
              : undefined
          }
        />
      )}
      {canWrite ? (
        <span className="sales-360-tag-add">
          <Select
            aria-label={t("sales.tags.addLabel")}
            value={picked}
            data-testid="sales-360-tag-pick"
            disabled={busy || offline || options.data === undefined || addable.length === 0}
            onChange={(e) => {
              setPicked(e.target.value);
            }}
          >
            <option value="">
              {options.data === undefined
                ? options.isError
                  ? t("sales.tags.optionsFailed")
                  : t("sales.common.loading")
                : addable.length === 0
                  ? t("sales.tags.noneToAdd")
                  : t("sales.tags.choose")}
            </option>
            {addable.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            data-testid="sales-360-tag-add"
            disabled={picked.length === 0 || offline}
            busy={busy}
            busyLabel={t("sales.common.saving")}
            onClick={() => {
              verb.mutate(
                { verb: "attach", tagId: picked },
                {
                  onSuccess: () => {
                    setPicked("");
                  },
                },
              );
            }}
          >
            {t("sales.tags.add")}
          </Button>
        </span>
      ) : null}
      {verb.isError ? (
        <MutationFailureNote
          data-testid="sales-360-tag-failed"
          text={refusal !== undefined ? t(refusal) : t("sales.tags.verbFailed")}
          error={verb.error}
        />
      ) : null}
    </div>
  );
}
